/**
 * Step 5 (shared): review the choices, set the limits, see its size, optionally
 * try one case, then save a draft or start. On start the server resolves
 * "production" to its number and freezes everything.
 */

import { LimitsFields } from "./experiment-fields";
import { formatCount, plural } from "@/lib/format";
import { useDatasetSource } from "@/features/datasets/hooks";
import { useExperimentSize } from "../hooks";
import type { SizeQuery } from "../types";
import { ScoreOneCase } from "./score-one-case";
import { TryOneCase } from "./try-one-case";
import { datasetLine, runnerLine, tracesLine } from "./step-values";
import type { NewExperiment } from "./use-new-experiment";

export function StepStart({ flow }: { flow: NewExperiment }) {
  const { form } = flow;
  return (
    <>
      <Summary flow={flow} />
      {flow.runner === "agent" && (
        <LimitsFields
          repetitions={form.repetitions}
          onRepetitions={form.setRepetitions}
          costLimit={form.costLimit}
          onCostLimit={form.setCostLimit}
          limitError={flow.limitError}
          maxCost={flow.maxCost}
        />
      )}
      {!flow.live && (
        <SizeLine
          query={
            flow.datasetId
              ? {
                  dataset_id: flow.datasetId,
                  evaluator_ids: flow.evaluatorIds,
                  repetitions: form.repetitions,
                }
              : null
          }
        />
      )}
      {flow.runner === "agent" ? <TryOneCase flow={flow} /> : <ScoreOneCase flow={flow} />}
    </>
  );
}

/** What the earlier steps picked, one line each. */
function Summary({ flow }: { flow: NewExperiment }) {
  const { form, target } = flow;
  const limit = useDatasetSource(flow.datasetId ?? "", flow.live).data?.limit;
  const what = [target.resolved.group, target.resolved.target].filter(Boolean).join(" / ");
  const rows: Array<[string, string]> = [
    ["Name", form.name.trim()],
    ...(form.description.trim() ? [["Description", form.description.trim()] as [string, string]] : []),
    ["Dataset", datasetLine(flow)],
    ...(flow.live ? [["Traces", tracesLine(flow, limit)] as [string, string]] : []),
    ["Runner", runnerLine(flow, what)],
    ["Evaluators", plural(flow.selected.size, "evaluator")],
  ];
  return (
    <dl aria-label="Summary" className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-1.5 text-[13px]">
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-muted">{label}</dt>
          <dd className="min-w-0 break-words">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** "120 items × 1 repetition × 3 evaluators = 360 checks (240 paid)", from the server. */
function SizeLine({ query }: { query: SizeQuery | null }) {
  const size = useExperimentSize(query);
  if (!query || query.evaluator_ids.length === 0)
    return <span className="text-xs text-muted">Pick a dataset and at least one evaluator.</span>;
  if (size.isPending) return <span className="text-xs text-muted">Counting checks…</span>;
  if (size.isError || !size.data) return <span className="text-xs text-muted">Could not count checks.</span>;
  const s = size.data;
  return (
    <span className="flex flex-col gap-0.5" aria-label="Experiment size">
      <span className="font-mono text-[15px] font-medium">{plural(s.checks, "check")}</span>
      <span className="text-xs text-muted">
        {plural(s.items, "item")} × {plural(s.repetitions, "repetition")} ×{" "}
        {plural(s.evaluators, "evaluator")} = {formatCount(s.checks)} checks ({formatCount(s.paid_checks)}{" "}
        paid)
      </span>
    </span>
  );
}
